
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    Badge,
    Box,
    Button,
    Card,
    Container,
    Divider,
    Group,
    Loader,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from "@mantine/core";

import {
    IconAlertCircle,
    IconCheck,
    IconClock,
    IconCreditCard,
    IconEye,
    IconRefresh,
    IconSearch,
    IconX,
} from "@tabler/icons-react";

import { getAuthHeaders } from "../../../api/authHelper";

type PaymentStatus =
    | "Pending"
    | "Approved"
    | "Rejected"
    | "Paid";

type AdminEventDto = {
    eventId: number;
    name: string;
};

type PaymentRequest = {
    id: number;
    eventId: number;
    personId: number;
    beneficiaryName: string;
    beneficiaryAddress: string;
    email: string;
    bankName: string;
    bankAddress: string;
    accountNumber: string;
    iban: string;
    bicSwiftAddress: string;
    comment: string | null;
    status: string;
    createdAt: string;
    paidAt: string | null;
};

const apiUrl = (
    process.env.REACT_APP_API_URL || ""
).replace(/\/+$/, "");

// GET requests: authorization only.
// No Content-Type is needed without a body.
function getHeaders(): HeadersInit {
    return {
        ...getAuthHeaders(),
    };
}

// PATCH requests: authorization AND JSON content type.
// This prevents HTTP 415 Unsupported Media Type.
function jsonHeaders(): HeadersInit {
    return {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
    };
}

function statusColor(status: string): string {
    switch (status.toLowerCase()) {
        case "approved":
            return "blue";
        case "paid":
            return "green";
        case "rejected":
            return "red";
        default:
            return "yellow";
    }
}

function formatDate(value?: string | null): string {
    if (!value) return "—";

    const date = new Date(value);

    return Number.isNaN(date.getTime())
        ? "—"
        : date.toLocaleString("da-DK");
}

async function apiError(response: Response): Promise<string> {
    try {
        const body = await response.json();

        return (
            body.message ||
            body.title ||
            `API error ${response.status}`
        );
    } catch {
        return `API error ${response.status}`;
    }
}

export default function AdminPaymentRequestsPage() {
    const [events, setEvents] = useState<AdminEventDto[]>([]);
    const [eventId, setEventId] = useState<number | null>(null);
    const [eventsLoading, setEventsLoading] = useState(true);

    const [items, setItems] = useState<PaymentRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("All");

    const [selected, setSelected] =
        useState<PaymentRequest | null>(null);

    const [nextStatus, setNextStatus] =
        useState<PaymentStatus | null>(null);

    const [confirmOpen, setConfirmOpen] = useState(false);

    // -----------------------------------
    // EVENTS
    // -----------------------------------

    useEffect(() => {
        let active = true;

        async function loadEvents() {
            setEventsLoading(true);

            try {
                const response = await fetch(
                    `${apiUrl}/api/experience/admin/events`,
                    {
                        method: "GET",
                        headers: getHeaders(),
                        cache: "no-store",
                    }
                );

                if (!response.ok) {
                    throw new Error(await apiError(response));
                }

                const data: AdminEventDto[] =
                    await response.json();

                if (!active) return;

                setEvents(data);

                if (data.length > 0) {
                    setEventId(data[0].eventId);
                }
            } catch (err) {
                if (!active) return;

                setError(
                    err instanceof Error
                        ? err.message
                        : "Could not load events."
                );
            } finally {
                if (active) {
                    setEventsLoading(false);
                }
            }
        }

        void loadEvents();

        return () => {
            active = false;
        };
    }, []);

    const eventOptions = useMemo(
        () =>
            events.map(event => ({
                value: String(event.eventId),
                label: event.name,
            })),
        [events]
    );

    // -----------------------------------
    // PAYMENT REQUESTS
    // -----------------------------------

    const load = useCallback(async () => {
        if (!eventId) {
            setItems([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await fetch(
                `${apiUrl}/api/payment-requests/admin/events/${eventId}`,
                {
                    method: "GET",
                    headers: getHeaders(),
                    cache: "no-store",
                }
            );

            if (!response.ok) {
                throw new Error(await apiError(response));
            }

            const data: PaymentRequest[] =
                await response.json();

            setItems(data);
        } catch (err) {
            setItems([]);

            setError(
                err instanceof Error
                    ? err.message
                    : "Could not load allowance requests."
            );
        } finally {
            setLoading(false);
        }
    }, [eventId]);

    useEffect(() => {
        void load();
    }, [load]);

    function changeEvent(value: string | null) {
        setEventId(value ? Number(value) : null);
        setItems([]);
        setSelected(null);
        setConfirmOpen(false);
        setNextStatus(null);
        setSearch("");
        setFilter("All");
        setError("");
        setNotice("");
    }

    // -----------------------------------
    // STATISTICS AND FILTERS
    // -----------------------------------

    const counts = useMemo(() => {
        const count = (status: string) =>
            items.filter(
                x => x.status.toLowerCase() === status.toLowerCase()
            ).length;

        return {
            all: items.length,
            pending: count("Pending"),
            approved: count("Approved"),
            rejected: count("Rejected"),
            paid: count("Paid"),
        };
    }, [items]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();

        return items
            .filter(item => {
                const matchesStatus =
                    filter === "All" ||
                    item.status.toLowerCase() ===
                    filter.toLowerCase();

                const matchesSearch =
                    !q ||
                    [
                        item.beneficiaryName,
                        item.email,
                        item.bankName,
                        String(item.personId),
                    ].some(value =>
                        (value ?? "").toLowerCase().includes(q)
                    );

                return matchesStatus && matchesSearch;
            })
            .sort(
                (a, b) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
            );
    }, [items, search, filter]);

    // -----------------------------------
    // STATUS MANAGEMENT
    // -----------------------------------

    function requestStatus(status: PaymentStatus) {
        if (!selected || saving) return;

        setNextStatus(status);
        setConfirmOpen(true);
    }

    async function saveStatus() {
        if (!selected || !nextStatus || saving) return;

        setSaving(true);
        setError("");
        setNotice("");

        const requestId = selected.id;
        const status = nextStatus;

        try {
            const response = await fetch(
                `${apiUrl}/api/payment-requests/admin/${requestId}/status`,
                {
                    method: "PATCH",
                    headers: jsonHeaders(),
                    body: JSON.stringify({
                        status,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(await apiError(response));
            }

            const result = await response.json();

            const updated: PaymentRequest = {
                ...selected,
                status: result.status ?? status,
                paidAt: result.paidAt ?? (
                    status === "Pending" ? null : selected.paidAt
                ),
            };

            setItems(previous =>
                previous.map(item =>
                    item.id === requestId ? updated : item
                )
            );

            setSelected(updated);
            setConfirmOpen(false);
            setNextStatus(null);

            setNotice(
                `Allowance Request #${requestId} updated to ${status}.`
            );

            await load();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Could not update allowance request."
            );
        } finally {
            setSaving(false);
        }
    }

    function detail(label: string, value?: string | null) {
        return (
            <Box>
                <Text size="xs" c="dimmed" fw={600}>
                    {label}
                </Text>

                <Text
                    size="sm"
                    fw={500}
                    style={{
                        overflowWrap: "anywhere",
                        whiteSpace: "pre-wrap",
                    }}
                >
                    {value || "—"}
                </Text>
            </Box>
        );
    }

    // -----------------------------------
    // RENDER
    // -----------------------------------

    return (
        <Container size="xl" py="xl">
            <Stack gap="lg">
                <Paper withBorder p="lg" radius="md">
                    <Group
                        justify="space-between"
                        align="flex-end"
                        wrap="wrap"
                    >
                        <Stack gap={2}>
                            <Title order={2}>
                                Allowance Requests
                            </Title>

                            <Text c="dimmed" size="sm">
                                Administration of umpire allowance requests
                            </Text>
                        </Stack>

                        <Group align="flex-end" wrap="wrap">
                            <Select
                                label="Vælg event"
                                placeholder="Select event..."
                                data={eventOptions}
                                value={
                                    eventId
                                        ? String(eventId)
                                        : null
                                }
                                onChange={changeEvent}
                                searchable
                                w={320}
                                disabled={eventsLoading}
                                rightSection={
                                    eventsLoading
                                        ? <Loader size="xs" />
                                        : undefined
                                }
                            />

                            <Button
                                color="red"
                                variant="light"
                                leftSection={
                                    <IconRefresh size={17} />
                                }
                                onClick={() => void load()}
                                loading={loading}
                                disabled={!eventId}
                            >
                                Refresh
                            </Button>
                        </Group>
                    </Group>
                </Paper>

                {error && (
                    <Alert
                        color="red"
                        title="Error"
                        icon={<IconAlertCircle size={18} />}
                    >
                        {error}
                    </Alert>
                )}

                {notice && (
                    <Alert
                        color="green"
                        title="Updated"
                        icon={<IconCheck size={18} />}
                    >
                        {notice}
                    </Alert>
                )}

                {!eventId ? (
                    <Paper withBorder p="xl" radius="md">
                        <Text ta="center" c="dimmed">
                            Select an event to view allowance requests.
                        </Text>
                    </Paper>
                ) : (
                    <>
                        <SimpleGrid
                            cols={{ base: 2, sm: 5 }}
                            spacing="sm"
                        >
                            {[
                                {
                                    label: "All",
                                    value: counts.all,
                                    color: "gray",
                                },
                                {
                                    label: "Pending",
                                    value: counts.pending,
                                    color: "yellow",
                                },
                                {
                                    label: "Approved",
                                    value: counts.approved,
                                    color: "blue",
                                },
                                {
                                    label: "Rejected",
                                    value: counts.rejected,
                                    color: "red",
                                },
                                {
                                    label: "Paid",
                                    value: counts.paid,
                                    color: "green",
                                },
                            ].map(stat => (
                                <Card
                                    key={stat.label}
                                    withBorder
                                    radius="md"
                                    p="md"
                                >
                                    <Text size="xs" c="dimmed">
                                        {stat.label}
                                    </Text>

                                    <Text
                                        size="xl"
                                        fw={800}
                                        c={stat.color}
                                    >
                                        {stat.value}
                                    </Text>
                                </Card>
                            ))}
                        </SimpleGrid>

                        <Paper withBorder p="md" radius="md">
                            <Group align="end">
                                <TextInput
                                    style={{
                                        flex: 1,
                                        minWidth: 200,
                                    }}
                                    label="Search"
                                    placeholder="Name, email, bank or person ID"
                                    leftSection={
                                        <IconSearch size={16} />
                                    }
                                    value={search}
                                    onChange={e =>
                                        setSearch(e.currentTarget.value)
                                    }
                                />

                                <Select
                                    label="Status"
                                    w={180}
                                    value={filter}
                                    onChange={value =>
                                        setFilter(value || "All")
                                    }
                                    data={[
                                        "All",
                                        "Pending",
                                        "Approved",
                                        "Rejected",
                                        "Paid",
                                    ]}
                                    allowDeselect={false}
                                />
                            </Group>
                        </Paper>

                        {loading ? (
                            <Group justify="center" py="xl">
                                <Loader color="red" />
                            </Group>
                        ) : (
                            <Paper withBorder radius="md">
                                <Table.ScrollContainer minWidth={800}>
                                    <Table
                                        striped
                                        highlightOnHover
                                        verticalSpacing="md"
                                    >
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Name</Table.Th>
                                                <Table.Th>Email</Table.Th>
                                                <Table.Th>Bank</Table.Th>
                                                <Table.Th>Submitted</Table.Th>
                                                <Table.Th>Status</Table.Th>
                                                <Table.Th />
                                            </Table.Tr>
                                        </Table.Thead>

                                        <Table.Tbody>
                                            {filtered.map(item => (
                                                <Table.Tr key={item.id}>
                                                    <Table.Td>
                                                        <Text
                                                            fw={600}
                                                            size="sm"
                                                        >
                                                            {item.beneficiaryName}
                                                        </Text>

                                                        <Text
                                                            size="xs"
                                                            c="dimmed"
                                                        >
                                                            Person #{item.personId}
                                                        </Text>
                                                    </Table.Td>

                                                    <Table.Td>
                                                        {item.email}
                                                    </Table.Td>

                                                    <Table.Td>
                                                        {item.bankName}
                                                    </Table.Td>

                                                    <Table.Td>
                                                        {formatDate(
                                                            item.createdAt
                                                        )}
                                                    </Table.Td>

                                                    <Table.Td>
                                                        <Badge
                                                            color={statusColor(
                                                                item.status
                                                            )}
                                                            variant="light"
                                                        >
                                                            {item.status}
                                                        </Badge>
                                                    </Table.Td>

                                                    <Table.Td>
                                                        <Button
                                                            size="xs"
                                                            color="red"
                                                            variant="light"
                                                            leftSection={
                                                                <IconEye
                                                                    size={15}
                                                                />
                                                            }
                                                            onClick={() =>
                                                                setSelected(item)
                                                            }
                                                        >
                                                            View
                                                        </Button>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                </Table.ScrollContainer>

                                {filtered.length === 0 && (
                                    <Text
                                        ta="center"
                                        c="dimmed"
                                        py="xl"
                                    >
                                        No allowance requests found.
                                    </Text>
                                )}
                            </Paper>
                        )}
                    </>
                )}
            </Stack>

            {/* DETAILS MODAL */}
            <Modal
                opened={!!selected}
                onClose={() => {
                    if (!saving) {
                        setSelected(null);
                    }
                }}
                title={
                    <Text fw={700}>
                        Allowance Request #{selected?.id}
                    </Text>
                }
                size="lg"
                centered
            >
                {selected && (
                    <Stack gap="md">
                        <Group justify="space-between">
                            <Text size="sm" c="dimmed">
                                Submitted{" "}
                                {formatDate(selected.createdAt)}
                            </Text>

                            <Badge
                                color={statusColor(selected.status)}
                            >
                                {selected.status}
                            </Badge>
                        </Group>

                        <Divider
                            label="Beneficiary"
                            labelPosition="left"
                        />

                        {detail(
                            "Name",
                            selected.beneficiaryName
                        )}

                        {detail(
                            "Address",
                            selected.beneficiaryAddress
                        )}

                        {detail(
                            "Email",
                            selected.email
                        )}

                        <Divider
                            label="Bank details"
                            labelPosition="left"
                        />

                        {detail(
                            "Bank name",
                            selected.bankName
                        )}

                        {detail(
                            "Bank address",
                            selected.bankAddress
                        )}

                        {detail(
                            "Account number",
                            selected.accountNumber
                        )}

                        {detail(
                            "IBAN",
                            selected.iban
                        )}

                        {detail(
                            "BIC / SWIFT",
                            selected.bicSwiftAddress
                        )}

                        <Divider
                            label="Additional information"
                            labelPosition="left"
                        />

                        {detail(
                            "Comment",
                            selected.comment
                        )}

                        {detail(
                            "Paid at",
                            formatDate(selected.paidAt)
                        )}

                        <Divider
                            label="Actions"
                            labelPosition="left"
                        />

                        {selected.status === "Pending" && (
                            <Group grow>
                                <Button
                                    color="green"
                                    leftSection={
                                        <IconCheck size={17} />
                                    }
                                    onClick={() =>
                                        requestStatus("Approved")
                                    }
                                >
                                    Approve
                                </Button>

                                <Button
                                    color="red"
                                    variant="light"
                                    leftSection={
                                        <IconX size={17} />
                                    }
                                    onClick={() =>
                                        requestStatus("Rejected")
                                    }
                                >
                                    Reject
                                </Button>
                            </Group>
                        )}

                        {selected.status === "Approved" && (
                            <Button
                                color="green"
                                leftSection={
                                    <IconCreditCard size={17} />
                                }
                                onClick={() =>
                                    requestStatus("Paid")
                                }
                            >
                                Mark as paid
                            </Button>
                        )}

                        {selected.status === "Rejected" && (
                            <Button
                                color="blue"
                                variant="light"
                                leftSection={
                                    <IconClock size={17} />
                                }
                                onClick={() =>
                                    requestStatus("Pending")
                                }
                            >
                                Reopen request
                            </Button>
                        )}

                        {selected.status === "Paid" && (
                            <Alert
                                color="green"
                                icon={<IconCheck size={18} />}
                            >
                                This request has been marked as paid.
                            </Alert>
                        )}
                    </Stack>
                )}
            </Modal>

            {/* CONFIRMATION MODAL */}
            <Modal
                opened={confirmOpen}
                onClose={() => {
                    if (!saving) {
                        setConfirmOpen(false);
                    }
                }}
                title="Confirm status change"
                centered
            >
                <Stack>
                    <Text size="sm">
                        Change request #{selected?.id} to{" "}
                        <strong>{nextStatus}</strong>?
                    </Text>

                    {nextStatus === "Paid" && (
                        <Alert color="yellow">
                            Only mark the request as paid after
                            the actual bank transfer has been completed.
                        </Alert>
                    )}

                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            disabled={saving}
                            onClick={() =>
                                setConfirmOpen(false)
                            }
                        >
                            Cancel
                        </Button>

                        <Button
                            color="red"
                            loading={saving}
                            onClick={() =>
                                void saveStatus()
                            }
                        >
                            Confirm
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Container>
    );
}
